import React, { useEffect, useState } from 'react';
import { DateRangePicker } from '../dashboard/DateRangePicker';
import { GranularitySelector, Granularity } from '../dashboard/GranularitySelector';
import { AuthenticationFilters } from './AuthenticationFilters';
import { EmptyState } from './EmptyState';
interface DateRange {
  start: Date;
  end: Date;
}
interface AuthenticationTabProps {
  dateRange: DateRange;
  granularity: Granularity;
  onDateRangeChange: (range: DateRange) => void;
  onGranularityChange: (granularity: Granularity) => void;
}
export function AuthenticationTab({
  dateRange,
  granularity,
  onDateRangeChange,
  onGranularityChange
}: AuthenticationTabProps) {
  // Filter states - no successRateMetric for Authentication tab
  const [deduplication, setDeduplication] = useState<'deduplicated' | 'raw'>('deduplicated');
  const [cardTypes, setCardTypes] = useState<string[]>([]);
  const [cardCountries, setCardCountries] = useState<string[]>([]);
  const [cardBrands, setCardBrands] = useState<string[]>([]);
  const [interactionTypes, setInteractionTypes] = useState<string[]>([]);
  const [additionalFilters, setAdditionalFilters] = useState<string[]>([]);
  // Calculate previous period date range
  const getPreviousPeriodRange = () => {
    const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
    const prevStart = new Date(dateRange.start);
    prevStart.setDate(prevStart.getDate() - days);
    const prevEnd = new Date(dateRange.end);
    prevEnd.setDate(prevEnd.getDate() - days);
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    };
    return `${formatDate(prevStart)} - ${formatDate(prevEnd)}`;
  };
  const handleClearFilters = () => {
    setDeduplication('deduplicated');
    setCardTypes([]);
    setCardCountries([]);
    setCardBrands([]);
    setInteractionTypes([]);
    setAdditionalFilters([]);
  };
  return <div className="space-y-6" data-id="element-726">
      {/* Main Filters Row */}
      <div className="flex flex-wrap items-center gap-3" data-id="element-727">
        <DateRangePicker value={dateRange} onChange={onDateRangeChange} data-id="element-728" />
        <GranularitySelector value={granularity as Granularity} onChange={onGranularityChange} data-id="element-729" />
        <AuthenticationFilters deduplication={deduplication} onDeduplicationChange={setDeduplication} cardTypes={cardTypes} onCardTypesChange={setCardTypes} cardCountries={cardCountries} onCardCountriesChange={setCardCountries} cardBrands={cardBrands} onCardBrandsChange={setCardBrands} interactionTypes={interactionTypes} onInteractionTypesChange={setInteractionTypes} additionalFilters={additionalFilters} onAdditionalFiltersChange={setAdditionalFilters} data-id="element-730" />
      </div>

      {/* Key Metrics Section */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-731">
        <div className="p-6 border-b border-gray-200" data-id="element-732">
          <div className="flex items-center justify-between" data-id="element-733">
            <div className="flex items-center gap-4" data-id="element-734">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-735">
                Key metrics
              </h2>
              <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-736">
                <span data-id="element-737">Compared to</span>
                <button className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-738">
                  Previous period
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" data-id="element-739">
                    <path d="M4.427 9.573l3.396-3.396a.25.25 0 01.354 0l3.396 3.396a.25.25 0 01-.177.427H4.604a.25.25 0 01-.177-.427z" data-id="element-740" />
                  </svg>
                </button>
                <span className="text-gray-500" data-id="element-741">
                  {getPreviousPeriodRange()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-12" data-id="element-742">
          <EmptyState message="No authentication requests matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-744">
                  Clear filters
                </button>{' '}
                or select another date range and try again.
              </>} data-id="element-743" />
        </div>
      </div>

      {/* 3D Secure authentication outcomes */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-745">
        <div className="p-6 border-b border-gray-200" data-id="element-746">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-747">
            3D Secure authentication outcomes
          </h2>
        </div>

        <div className="p-12" data-id="element-748">
          <EmptyState message="No authentication requests matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-750">
                  Clear filters
                </button>{' '}
                or select another date range and try again.
              </>} data-id="element-749" />
        </div>
      </div>

      {/* 3D Secure authentication request reasons */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-751">
        <div className="p-6 border-b border-gray-200" data-id="element-752">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-753">
            3D Secure authentication request reasons
          </h2>
        </div>

        <div className="p-12" data-id="element-754">
          <EmptyState message="No authentication requests matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-756">
                  Clear filters
                </button>{' '}
                or select another date range and try again.
              </>} data-id="element-755" />
        </div>
      </div>
    </div>;
}